<template>
  <div class="inspection-status-badge" :class="badgeClasses">
    <span class="inspection-status-badge__dot" aria-hidden="true"></span>
    <span class="inspection-status-badge__label">{{ label }}</span>
  </div>
</template>

<script setup>
import { computed } from "vue";

/**
 * Status badge for InspectionRequest.status.
 * Figma node 31406:220372 (InspectionRequestStatusBadge): fully rounded pill with a
 * leading colour dot, Inter 500 12/18, one fill + border + dot colour per state.
 *
 * Stored statuses keep their values; only the labels differ: "passed" / "done" show as
 * "Completed" and "abandoned" shows as "Declined" (EC Inspection Requests, ER-12798).
 */
const props = defineProps({
  status: {
    type: String,
    required: true,
    validator(value) {
      const normalized = value?.toLowerCase().replace(/\s+/g, "_") || "";
      return ["pending", "accepted", "completed", "passed", "done", "failed", "declined", "abandoned", "canceled"].includes(normalized);
    },
  },
});

const STATUS_LABELS = {
  pending: "Pending",
  accepted: "Accepted",
  completed: "Completed",
  passed: "Completed",
  done: "Completed",
  failed: "Failed",
  declined: "Declined",
  abandoned: "Declined",
  canceled: "Canceled",
};

const normalizedStatus = computed(() => {
  return props.status?.toLowerCase().replace(/\s+/g, "_") || "";
});

const label = computed(() => STATUS_LABELS[normalizedStatus.value] || props.status);

const badgeClasses = computed(() => {
  const statusClass = normalizedStatus.value.replace(/_/g, "-");
  return {
    [`inspection-status-badge--${statusClass}`]: true,
  };
});
</script>

<style scoped>
.inspection-status-badge {
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
  text-align: center;
  font-family: Inter;
}

.inspection-status-badge__dot {
  width: 8px;
  height: 8px;
  border-radius: 9999px;
  flex-shrink: 0;
}

/* Figma-spec pill: radius-full, pad 2/8/2/6, gap 4, Inter 500 12/18 */
.inspection-status-badge {
  gap: 4px;
  padding: 2px 8px 2px 6px;
  border-radius: 9999px;
  border: 1px solid transparent;
  font-weight: 500;
  font-size: 12px;
  line-height: 18px;
}

/* Pending */
.inspection-status-badge--pending {
  background-color: #EFF8FF;
  color: #175CD3;
  border-color: #B2DDFF;
}
.inspection-status-badge--pending .inspection-status-badge__dot {
  background-color: #2E90FA;
}

/* Accepted, Completed (passed / done) */
.inspection-status-badge--accepted,
.inspection-status-badge--completed,
.inspection-status-badge--passed,
.inspection-status-badge--done {
  background-color: #ECFDF3;
  color: #067647;
  border-color: #ABEFC6;
}
.inspection-status-badge--accepted .inspection-status-badge__dot,
.inspection-status-badge--completed .inspection-status-badge__dot,
.inspection-status-badge--passed .inspection-status-badge__dot,
.inspection-status-badge--done .inspection-status-badge__dot {
  background-color: #17B26A;
}

/* Declined (abandoned) and Failed */
.inspection-status-badge--declined,
.inspection-status-badge--abandoned,
.inspection-status-badge--failed {
  background-color: #FEF3F2;
  color: #B42318;
  border-color: #FECDCA;
}
.inspection-status-badge--declined .inspection-status-badge__dot,
.inspection-status-badge--abandoned .inspection-status-badge__dot,
.inspection-status-badge--failed .inspection-status-badge__dot {
  background-color: #F04438;
}

/* Canceled */
.inspection-status-badge--canceled {
  background-color: #FFFFFF;
  color: #344054;
  border: 2px solid #475467;
}
.inspection-status-badge--canceled .inspection-status-badge__dot {
  background-color: #475467;
}
</style>
